import { computed, ref, shallowRef } from 'vue';
import { storeToRefs } from 'pinia';
import * as Sentry from '@sentry/vue';
import type { Step } from '../types';
import { useSessionStore } from '@Stores';
import { INTAKE_TYPES } from '@IntakeModule';
import useCartStore from '../store';
import { CART_STEPS, OUTCOMES_TYPES } from '../types';
import { isCheckInIntakeType } from '../helpers';

import MCartStepAddProductsAndServices from '../components/molecules/MCartStep/MCartStepAddProductsAndServices/MCartStepAddProductsAndServices.vue';
import MCartStepAdoptionInfo from '../components/molecules/MCartStep/MCartStepAdoptionInfo/MCartStepAdoptionInfo.vue';
import MCartStepCheckoutSelector from '../components/molecules/MCartStep/MCartStepCheckoutSelector.vue';
import MCartStepCompleteFlow from '../components/molecules/MCartStep/MCartStepCompleteFlow/MCartStepCompleteFlow.vue';
import MCartStepDonations from '../components/molecules/MCartStep/MCartStepDonations/MCartStepDonations.vue';
import MCartStepMedicalCosts from '../components/molecules/MCartStep/MCartStepMedicalCosts/MCartStepMedicalCosts.vue';
import MCartStepMobileCheckout from '../components/molecules/MCartStep/MCartStepMobileCheckout.vue';
import MCartStepOutcomeInfo from '../components/molecules/MCartStep/MCartStepOutcomeInfo/MCartStepOutcomeInfo.vue';
import MCartStepPaymentMethod from '../components/molecules/MCartStep/MCartStepPaymentMethod/MCartStepPaymentMethod.vue';

const ADD_PEOPLE_STEP_HEADERS = {
  adopter: {
    tooltip: 'cart.tooltip.adopterInfo',
    header: {
      icon: 'faAddressCard',
      title: 'cart.stepper.adopter.title',
      subtitle: 'cart.stepper.adopter.subtitle',
    },
  },
  citation: {
    tooltip: 'cart.tooltip.adopterInfo',
    header: {
      icon: 'faAddressCard',
      title: 'cart.stepper.citation.title',
      subtitle: 'cart.stepper.citation.subtitle',
    },
  },
  guardian: {
    tooltip: 'cart.tooltip.summary',
    header: {
      icon: 'faUser',
      title: 'cart.stepper.guardian.title',
      subtitle: 'cart.stepper.guardian.subtitle',
    },
  },
  payor: {
    tooltip: 'cart.tooltip.summary',
    header: {
      icon: 'faUser',
      title: 'cart.stepper.payor.title',
      subtitle: 'cart.stepper.payor.subtitle',
    },
  },
  product: {
    tooltip: 'cart.tooltip.summary',
    header: {
      icon: 'faUser',
      title: 'cart.stepper.buyer.title',
      subtitle: 'cart.stepper.buyer.subtitle',
    },
  },
  donation: {
    tooltip: 'cart.tooltip.summary',
    header: {
      icon: 'faUser',
      title: 'cart.stepper.donor.title',
      subtitle: 'cart.stepper.donor.subtitle',
    },
  },
  contact: {
    tooltip: 'cart.tooltip.summary',
    header: {
      icon: 'faUser',
      title: 'cart.stepper.contact.title',
      subtitle: 'cart.stepper.contact.subtitle',
    },
  },
};

const currentFlow = ref<CART_STEPS[]>([]);
const currentStep = ref<CART_STEPS>(CART_STEPS.OUTCOME_INFO);

const STEPS = ref<Step[]>([
  {
    active: false,
    component: shallowRef(MCartStepOutcomeInfo),
    name: CART_STEPS.OUTCOME_INFO,
    tooltip: 'cart.tooltip.outcomeInfo',
    header: {
      icon: 'faListCheck',
      title: 'cart.stepper.outcomeInfo.title',
      subtitle: 'cart.stepper.outcomeInfo.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(MCartStepAddProductsAndServices),
    name: CART_STEPS.PRODUCTS_AND_SERVICES,
    tooltip: 'cart.tooltip.addProducts',
    header: {
      icon: 'faBox',
      title: 'cart.stepper.products.title',
      subtitle: 'cart.stepper.products.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(MCartStepCheckoutSelector),
    name: CART_STEPS.CHECKOUT_METHOD,
    tooltip: 'cart.tooltip.checkoutMethod',
    header: {
      icon: 'faFileInvoiceDollar',
      title: 'cart.stepper.checkoutMethod.title',
      subtitle: 'cart.stepper.checkoutMethod.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(MCartStepAdoptionInfo),
    name: CART_STEPS.ADD_PEOPLE,
    tooltip: 'cart.tooltip.adopterInfo',
    header: {
      icon: 'faAddressCard',
      title: 'cart.stepper.adopter.title',
      subtitle: 'cart.stepper.adopter.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(MCartStepMobileCheckout),
    name: CART_STEPS.MOBILE_CHECKOUT,
    tooltip: 'cart.tooltip.mobileCheckout',
    header: {
      icon: 'faMobile',
      title: 'cart.stepper.pendingMobileCheckout.title',
      subtitle: 'cart.stepper.pendingMobileCheckout.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(MCartStepPaymentMethod),
    name: CART_STEPS.PAYMENT_METHOD,
    tooltip: 'cart.tooltip.paymentMethod',
    header: {
      icon: 'faWallet',
      title: 'cart.stepper.paymentMethod.title',
      subtitle: 'cart.stepper.paymentMethod.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(MCartStepDonations),
    name: CART_STEPS.DONATION,
    tooltip: 'cart.tooltip.donation',
    header: {
      icon: 'faHandHoldingDollar',
      title: 'cart.stepper.donation.title',
      subtitle: 'cart.stepper.donation.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(MCartStepCompleteFlow),
    name: CART_STEPS.COMPLETE_CHECK_IN,
    tooltip: 'cart.tooltip.completeCheckIn',
    header: {
      icon: 'faCircleCheck',
      title: 'cart.stepper.completeFlow.titleCheckIn',
      subtitle: 'cart.stepper.completeFlow.subtitleCheckIn',
    },
  },
  {
    active: false,
    component: shallowRef(MCartStepMedicalCosts),
    name: CART_STEPS.MEDICAL_COSTS,
    tooltip: 'cart.tooltip.medicalCosts',
    header: {
      icon: 'faStethoscope',
      title: 'cart.stepper.medicalCosts.title',
      subtitle: 'cart.stepper.medicalCosts.subtitle',
    },
  },
]);

const previousStep = computed(() => {
  const previousStepIndex = currentFlow.value.indexOf(currentStep.value) - 1;
  return currentFlow.value[previousStepIndex];
});

const nextStep = computed(() => {
  const nextStepIndex = currentFlow.value.indexOf(currentStep.value) + 1;
  const nextStepName = currentFlow.value[nextStepIndex];

  return {
    step: STEPS.value.find(({ name }) => name === nextStepName),
  };
});

const nextStepButton = computed(() => ({
  label: !nextStep.value.step ? 'common.complete' : 'common.nextStep',
  icon: nextStep.value.step ? nextStep.value.step.header.icon : '',
}));

export default function () {
  const { shelterHasProducts, shelterProfile } = storeToRefs(useSessionStore());

  // CART_STEPS and OUTCOMES_TYPES enums
  const {
    ADD_PEOPLE,
    CHECKOUT_METHOD,
    COMPLETE_CHECK_IN,
    DONATION,
    MEDICAL_COSTS,
    MOBILE_CHECKOUT,
    OUTCOME_INFO,
    PAYMENT_METHOD,
    PRODUCTS_AND_SERVICES,
  } = CART_STEPS;

  const {
    // IN CARE
    ADOPTED,
    CLINIC_OUT,
    DIED_IN_CARE,
    DOA,
    LOST,
    OWNER_REQUESTED_EUTHANASIA,
    RETURN_TO_FIELD,
    RETURN_TO_OWNER,
    SERVICE_OUT,
    SHELTER_EUTHANASIA,
    TRANSFER_OUT,
    WILDLIFE_OUT,

    // LOST & FOUND
    FINDER_KEPT,
    REHOMED,
    REUNITED_WITH_OWNER,
    SHELTER_INTAKE,
    UNKNOWN,

    // REHOME
    REHOME,
    REHOME_SHELTER_INTAKE,
    REHOME_UNKNOWN,
    REMAINED_WITH_OWNER,

    // CITATION
    CITATION,

    // INTAKE
    INTAKE,

    // PRODUCTS
    PRODUCTS,

    // DONATIONS
    DONATIONS,
  } = OUTCOMES_TYPES;

  // NOTE :: CART TYPES
  const PLAIN_OUTCOME_TYPES = [
    DIED_IN_CARE,
    DOA,
    LOST,
    OWNER_REQUESTED_EUTHANASIA,
    REHOME,
    REHOME_SHELTER_INTAKE,
    REHOME_UNKNOWN,
    REMAINED_WITH_OWNER,
    RETURN_TO_FIELD,
    SHELTER_EUTHANASIA,
    SHELTER_INTAKE,
    TRANSFER_OUT,
    UNKNOWN,
    WILDLIFE_OUT,
  ];

  const OUTCOME_GUARDIAN_TYPES = [FINDER_KEPT, REUNITED_WITH_OWNER, REHOMED];

  const OUTCOMES_CART_TYPES = [ADOPTED, CLINIC_OUT, RETURN_TO_OWNER, SERVICE_OUT];

  // NOTE :: CART STEPS

  // PLAIN OUTCOME
  const PLAIN_OUTCOME_STEPS = [OUTCOME_INFO];

  // OUTCOME WITH GUARDIAN
  const OUTCOME_GUARDIAN_STEPS = [OUTCOME_INFO, ADD_PEOPLE];

  // CITATION
  const CITATION_CART_STEPS = [ADD_PEOPLE, DONATION, PAYMENT_METHOD];

  // DONATION
  const DONATION_CART_MANUAL_STEPS = [ADD_PEOPLE, DONATION, PAYMENT_METHOD];

  // INTAKE
  const INTAKE_CART_MANUAL_STEPS = [ADD_PEOPLE, CHECKOUT_METHOD, DONATION, PAYMENT_METHOD];
  const INTAKE_CART_MANUAL_STEPS_SIGNATURE_ONLY = [ADD_PEOPLE, CHECKOUT_METHOD, DONATION, COMPLETE_CHECK_IN];
  const INTAKE_CART_MOBILE_STEPS = [ADD_PEOPLE, CHECKOUT_METHOD, MOBILE_CHECKOUT];

  // PRODUCTS
  const PRODUCTS_CART_MANUAL_STEPS = [PRODUCTS_AND_SERVICES, CHECKOUT_METHOD, ADD_PEOPLE, DONATION, PAYMENT_METHOD];
  const PRODUCTS_CART_MOBILE_STEPS = [PRODUCTS_AND_SERVICES, CHECKOUT_METHOD, MOBILE_CHECKOUT];

  // OUTCOMES

  // OUTCOMES CART - NO PRODUCTS
  const OUTCOMES_CART_MOBILE_STEPS = [OUTCOME_INFO, CHECKOUT_METHOD, MOBILE_CHECKOUT];
  const OUTCOMES_CART_MANUAL_STEPS = [OUTCOME_INFO, CHECKOUT_METHOD, ADD_PEOPLE, DONATION, PAYMENT_METHOD];

  // OUTCOMES CART - WITH PRODUCTS
  const OUTCOMES_CART_MOBILE_WITH_PRODUCTS_STEPS = [
    OUTCOME_INFO,
    PRODUCTS_AND_SERVICES,
    CHECKOUT_METHOD,
    MOBILE_CHECKOUT,
  ];
  const OUTCOMES_CART_MANUAL_WITH_PRODUCTS_STEPS = [
    OUTCOME_INFO,
    PRODUCTS_AND_SERVICES,
    CHECKOUT_METHOD,
    ADD_PEOPLE,
    DONATION,
    PAYMENT_METHOD,
  ];

  const shelterIsTrackingMedicalCosts = computed(() => shelterProfile.value?.track_medical_costs);

  function getCheckoutHeader(step: Step, isIntake: boolean, isCheckInType: boolean) {
    const isCheckin = isIntake && isCheckInType;
    if (isCheckin) {
      return {
        ...step.header,
        title: 'cart.stepper.checkoutMethod.titleCheckin',
        subtitle: 'cart.stepper.checkoutMethod.subtitleCheckin',
      };
    }

    const isIntakeType = isIntake && !isCheckInType;
    if (isIntakeType) {
      return {
        ...step.header,
        title: 'cart.stepper.checkoutMethod.titleIntake',
        subtitle: 'cart.stepper.checkoutMethod.subtitleIntake',
      };
    }

    return {
      ...step.header,
      title: 'cart.stepper.checkoutMethod.title',
      subtitle: 'cart.stepper.checkoutMethod.subtitle',
    };
  }

  function resetSteps() {
    STEPS.value.forEach((step) => {
      step.active = false;

      // NOTE ::Reset ADD_PEOPLE step to default header (adopter)
      if (step.name === ADD_PEOPLE) {
        const { header, tooltip } = ADD_PEOPLE_STEP_HEADERS.adopter;
        step.header = header;
        step.tooltip = tooltip;
      }
    });

    currentFlow.value = [];
    currentStep.value = CART_STEPS.OUTCOME_INFO;
  }

  function setActiveOutcomeSteps(cartType?: OUTCOMES_TYPES, outcomeType?: OUTCOMES_TYPES | INTAKE_TYPES) {
    resetSteps();

    const type =
      cartType && [INTAKE, CITATION, PRODUCTS, DONATIONS].includes(cartType)
        ? cartType
        : outcomeType ?? cartType ?? null;

    if (!type) return;

    const isDonations = type === DONATIONS;
    const isIntake = type === INTAKE;
    const isCitation = type === CITATION;
    const isPlainOutcome = PLAIN_OUTCOME_TYPES.includes(type as OUTCOMES_TYPES);
    const isGuardianOutcome = OUTCOME_GUARDIAN_TYPES.includes(type as OUTCOMES_TYPES);
    const isCartOutcome = OUTCOMES_CART_TYPES.includes(type as OUTCOMES_TYPES);
    const isProduct = type === 'product';

    const hasProductStep = shelterHasProducts.value && !isIntake && !isCitation;

    // prettier-ignore
    switch (true) {
      case isDonations: return setOutcomeFlow(DONATION_CART_MANUAL_STEPS, 'donation');
      case isProduct: return setOutcomeFlow(PRODUCTS_CART_MOBILE_STEPS, 'product');
      case isIntake: {
        const isTransferIn = INTAKE_TYPES.TRANSFER_IN === outcomeType;
        const intakeFlow = isTransferIn ? INTAKE_CART_MANUAL_STEPS.slice(1) : INTAKE_CART_MANUAL_STEPS;
        const peopleStepType = isTransferIn ? undefined : 'contact';

        return setOutcomeFlow(intakeFlow, peopleStepType);
      }
      case isCitation: return setOutcomeFlow(CITATION_CART_STEPS, 'citation');
      case isPlainOutcome: return setOutcomeFlow(PLAIN_OUTCOME_STEPS);
      case isGuardianOutcome: return setOutcomeFlow(OUTCOME_GUARDIAN_STEPS, 'guardian');
      case isCartOutcome: {
        // prettier-ignore
        const outcomePeopleLabel = !outcomeType ? 'adopter'
          : [CLINIC_OUT, OWNER_REQUESTED_EUTHANASIA, SERVICE_OUT].includes(outcomeType as OUTCOMES_TYPES) ? 'guardian'
          : 'adopter';

        // NOTE :: Set product step
        const outcomeStepsFlow = hasProductStep ? OUTCOMES_CART_MOBILE_WITH_PRODUCTS_STEPS : OUTCOMES_CART_MOBILE_STEPS;

        // NOTE :: Set medical costs step
        if (shelterIsTrackingMedicalCosts.value) {
          const checkoutMethodIndex = outcomeStepsFlow.findIndex(step => step === CHECKOUT_METHOD);
          
          // NOTE :: This would be a developer error within the logic to update steps state
          const validStepIndex = checkoutMethodIndex > 0;
          if (!validStepIndex) {
            Sentry.captureException(new Error('CHECKOUT_METHOD not found in outcome steps flow'), {
              tags: {
                type: 'cart-flow',
                section: 'Medical Costs Step Integration',
              },
              extra: {
                outcomeType: type,
                hasProductStep,
                outcomeStepsFlow: outcomeStepsFlow.map(step => step.toString()),
                expectedStep: CHECKOUT_METHOD.toString(),
              },
            });
            setOutcomeFlow(outcomeStepsFlow, outcomePeopleLabel);
            return;
          }
          
          if (validStepIndex) outcomeStepsFlow.splice(checkoutMethodIndex - 1, 0, MEDICAL_COSTS);
        }
        
        setOutcomeFlow(outcomeStepsFlow, outcomePeopleLabel);
        return;
      }
      default: return setOutcomeFlow(PLAIN_OUTCOME_STEPS);
    }
  }

  function setOutcomeFlow(flow: CART_STEPS[], peopleStepType?: keyof typeof ADD_PEOPLE_STEP_HEADERS) {
    const activeSteps: Step[] = [];
    const inactiveSteps: Step[] = [];

    const { isIntake, outcomeType } = storeToRefs(useCartStore());
    const isCheckInType = isCheckInIntakeType(outcomeType.value);

    STEPS.value.forEach((step: Step) => {
      const peopleStepNeedsHeaderUpdate = step.name === ADD_PEOPLE && peopleStepType;
      if (peopleStepNeedsHeaderUpdate) {
        const { header, tooltip } = ADD_PEOPLE_STEP_HEADERS[peopleStepType];
        step.header = header;
        step.tooltip = tooltip;
      }
      const checkoutMethodNeedsHeaderUpdate = step.name === CHECKOUT_METHOD;
      if (checkoutMethodNeedsHeaderUpdate) {
        step.header = getCheckoutHeader(step, isIntake.value, isCheckInType);
      }

      const mobileCheckoutNeedsHeaderUpdate = step.name === MOBILE_CHECKOUT && isIntake.value;
      if (mobileCheckoutNeedsHeaderUpdate) {
        step.header = {
          ...step.header,
          // prettier-ignore
          title: !isIntake.value ? 'cart.stepper.pendingMobileCheckout.title'
            : isCheckInType ? 'cart.stepper.pendingMobileCheckout.titleCheckin'
            : 'cart.stepper.pendingMobileCheckout.titleIntake',

          // prettier-ignore
          subtitle: !isIntake.value ? 'cart.stepper.pendingMobileCheckout.subtitle'
            : isCheckInType ? 'cart.stepper.pendingMobileCheckout.subtitleCheckin'
            : 'cart.stepper.pendingMobileCheckout.subtitleIntake',
        };
      }
      const completeFlowNeedsHeaderUpdate = step.name === COMPLETE_CHECK_IN && isIntake.value;
      if (completeFlowNeedsHeaderUpdate) {
        step.header = {
          ...step.header,
          title: isCheckInType ? 'cart.stepper.completeFlow.titleCheckIn' : 'cart.stepper.completeFlow.titleIntake',
          subtitle: isCheckInType
            ? 'cart.stepper.completeFlow.subtitleCheckIn'
            : 'cart.stepper.completeFlow.subtitleIntake',
        };
      }
      const isActive = flow.includes(step.name);
      step.active = isActive;

      if (isActive) {
        const position = flow.indexOf(step.name);
        activeSteps[position] = step;
        return;
      }

      inactiveSteps.push(step);
    });

    STEPS.value = [...activeSteps, ...inactiveSteps];

    currentFlow.value = flow;
  }

  return {
    currentFlow,
    currentStep,
    nextStep,
    nextStepButton,
    previousStep,

    ADD_PEOPLE_STEP_HEADERS,
    OUTCOMES_CART_MANUAL_STEPS,
    OUTCOMES_CART_MANUAL_WITH_PRODUCTS_STEPS,
    OUTCOMES_CART_MOBILE_STEPS,
    OUTCOMES_CART_MOBILE_WITH_PRODUCTS_STEPS,
    OUTCOME_GUARDIAN_TYPES,
    PRODUCTS_CART_MANUAL_STEPS,
    PRODUCTS_CART_MOBILE_STEPS,
    INTAKE_CART_MANUAL_STEPS,
    INTAKE_CART_MANUAL_STEPS_SIGNATURE_ONLY,
    INTAKE_CART_MOBILE_STEPS,
    STEPS,

    setActiveOutcomeSteps,
    setOutcomeFlow,
  };
}
